import { useRef, useState, type ReactNode } from "react";
import clsx from "clsx";

// Carousel horizontal (scroll-snap) utilisé sur mobile à la place d'une
// grille — garde les sections compactes et évite l'empilement vertical de
// plusieurs grandes cartes. Générique : réutilisé sur la home (salles,
// activités) et sur la page tarifs (formules par catégorie).
export function MobileCarousel<T>({
  items,
  keyFn,
  renderItem,
  topPad = true,
}: {
  items: T[];
  keyFn: (item: T, i: number) => string;
  renderItem: (item: T, i: number) => ReactNode;
  /** Espace au-dessus des slides (nécessaire quand une carte déborde en haut, ex: badge "Le plus choisi"). */
  topPad?: boolean;
}) {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!items.length) return null;

  // Basé sur la position réelle des slides (pas juste scrollLeft / clientWidth,
  // qui ne colle pas à leur largeur de 85% + le gap) — sinon le dernier
  // indicateur n'est jamais atteint en fin de scroll.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollCenter = el.scrollLeft + el.clientWidth / 2;
    let closest = 0;
    let closestDist = Infinity;
    Array.from(el.children).forEach((child, i) => {
      const c = child as HTMLElement;
      const dist = Math.abs(c.offsetLeft + c.clientWidth / 2 - scrollCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    setActive(closest);
  };

  return (
    <div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={clsx(
          "-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          topPad && "pt-4",
        )}
      >
        {items.map((item, i) => (
          <div key={keyFn(item, i)} className="w-[85%] shrink-0 snap-center">
            {renderItem(item, i)}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {items.map((_, i) => (
            <span
              key={i}
              className={clsx(
                "h-1.5 rounded-full transition-all",
                i === active ? "w-6 bg-brand-500" : "w-1.5 bg-ink-300",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
